import { useLocalRuntime, AssistantRuntimeProvider } from '@assistant-ui/react';
import { Thread, FilesContext, MessageFilesContext, EmbeddedConfigContext } from './assistant-ui/thread';
import { useRef, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { ConversationStarters } from './ConversationStarters';
import { VoiceProvider } from '../lib/voice';
import chatEvents from '../lib/chatEvents';

// Separate component for runtime - remounts when key changes to reset chat
function ChatRuntimeWrapper({
  children,
  threadIdRef,
  temporaryDocumentsRef,
  customGptId,
  shareToken,
  API_URL,
  selectedFiles,
  setSelectedFiles,
  setMessageFiles
}) {
  // Use refs to always get the CURRENT value of props inside the async generator
  // This is critical because the generator closure captures stale values otherwise
  const selectedFilesRef = useRef(selectedFiles);
  const setSelectedFilesRef = useRef(setSelectedFiles);

  // Keep refs in sync with props
  useEffect(() => {
    selectedFilesRef.current = selectedFiles;
  }, [selectedFiles]);

  useEffect(() => {
    setSelectedFilesRef.current = setSelectedFiles;
  }, [setSelectedFiles]);

  // Forward events to parent window (for custom pages using hidden iframe)
  const notifyParent = (action, data) => {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ source: 'cme-embedded', action, ...data }, '*');
      }
    } catch (_) { /* cross-origin or no parent */ }
  };


  const runtime = useLocalRuntime({
    async *run({ messages, abortSignal }) {
      try {
        const lastMessage = messages[messages.length - 1];
        const userContent = lastMessage.content.find(c => c.type === 'text')?.text || '';

        // Get the CURRENT files from the ref (not the stale closure value)
        const filesToSend = [...selectedFilesRef.current];

        // Clear files immediately so the UI updates right away
        setSelectedFilesRef.current([]);

        const currentFiles = filesToSend.map(file => ({
          id: URL.createObjectURL(file),
          filename: file.name,
          content_type: file.type,
          size: file.size
        }));

        setMessageFiles(prev => [...prev, currentFiles]);

        const formData = new FormData();

        // Check for temporary files to include
        const tempFiles = temporaryDocumentsRef.current;

        const payload = {
          type: 'items.create',
          params: {
            thread_id: threadIdRef.current,
            message: userContent,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            // Include temporary file IDs if any were set via postMessage/uploadTemporaryDocuments
            ...(tempFiles && tempFiles.length > 0 && {
              temporary_file_ids: tempFiles
            })
          }
        };

        formData.append('payload', JSON.stringify(payload));

        filesToSend.forEach((file, index) => {
          formData.append(`file_${index}`, file);
        });

        // Add share token to payload for public access
        if (shareToken) {
          const updatedPayload = {
            ...payload,
            token: shareToken
          };
          formData.set('payload', JSON.stringify(updatedPayload));
        }

        const headers = {};
        // No Authorization header for public embedded mode

        const response = await fetch(`${API_URL}/public/cme/${customGptId}`, {
          method: 'POST',
          headers: headers,
          body: formData,
          signal: abortSignal
        });

        // Clear temporary documents after sending (they're only for the first message)
        if (tempFiles && tempFiles.length > 0) {
          temporaryDocumentsRef.current = [];
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        notifyParent('streamStart', { message: userContent });

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulatedText = '';
        let currentStatus = null;
        let userMessageId = null;
        let assistantMessageId = null;
        // Track all non-text content parts (images, charts, file downloads) to preserve them
        let accumulatedParts = [];

        // Helper to build content array with all accumulated parts
        const buildContentParts = (includeStatus = null) => {
          const parts = [];
          if (accumulatedText) {
            let displayText = chatEvents.transformText(accumulatedText);
            displayText = displayText.replace(/\[\[[^\]]*$/, '');
            parts.push({ type: 'text', text: displayText });
          }
          // Add all accumulated non-text parts (images, charts, etc.)
          parts.push(...accumulatedParts);
          if (includeStatus) {
            parts.push(includeStatus);
          }
          return parts;
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim() || !line.startsWith('data: ')) continue;

            try {
              const jsonStr = line.slice(6);
              const parsed = JSON.parse(jsonStr);

              if (parsed.type === 'status_event') {
                // Status events - show immediately (not typewritten)
                if (parsed.content && parsed.content[0]) {
                  const statusType = parsed.content[0].type || 'log';
                  const statusText = parsed.content[0].text;
                  currentStatus = { type: statusType, text: statusText };
                  notifyParent('streamStatus', { statusType, text: statusText });
                  yield { content: buildContentParts({ type: 'status', statusType, text: statusText }) };
                }
              } else if (parsed.type === 'thread_stream') {
                // Check for different content types in thread_stream
                const textContent = parsed.content.find(c => c.type === 'text');
                const imageContent = parsed.content.find(c => c.type === 'image');

                if (imageContent && imageContent.data) {
                  // Image content - add to accumulated parts and show immediately
                  currentStatus = null;
                  accumulatedParts.push({
                    type: 'image',
                    data: imageContent.data,
                    mime_type: imageContent.mime_type || 'image/png'
                  });
                  notifyParent('streamImage', { data: imageContent.data, mimeType: imageContent.mime_type || 'image/png' });
                  yield { content: buildContentParts() };
                } else if (textContent && textContent.text) {
                  // Text content - this gets typewritten
                  accumulatedText += textContent.text;
                  currentStatus = null;
                  notifyParent('streamText', { chunk: textContent.text, fullText: accumulatedText });
                  yield { content: buildContentParts() };
                  chatEvents.dispatchEvent(new CustomEvent('tts_text', { detail: { text: textContent.text } }));
                }
              } else if (parsed.type === 'chart') {
                // Chart - add to accumulated parts and show immediately
                if (parsed.metadata && parsed.metadata.chart_html) {
                  currentStatus = null;
                  accumulatedParts.push({
                    type: 'chart',
                    chartHtml: parsed.metadata.chart_html,
                    chartId: parsed.metadata.chart_id,
                    chartType: parsed.metadata.chart_type
                  });
                  notifyParent('streamChart', { chartHtml: parsed.metadata.chart_html, chartId: parsed.metadata.chart_id, chartType: parsed.metadata.chart_type });
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'message_complete') {
                // Complete message - show immediately (tool results, etc.)
                if (parsed.content && parsed.content[0] && parsed.content[0].text) {
                  accumulatedText = parsed.content[0].text;
                  currentStatus = null;
                  notifyParent('streamText', { chunk: '', fullText: accumulatedText });
                  yield {
                    content: [{ type: 'tool-result', text: accumulatedText }]
                  };
                }
              } else if (parsed.type === 'file_download') {
                // File download - add to accumulated parts and show immediately
                if (parsed.metadata && parsed.metadata.file_data && parsed.metadata.filename) {
                  currentStatus = null;
                  accumulatedParts.push({
                    type: 'file_download',
                    filename: parsed.metadata.filename,
                    fileData: parsed.metadata.file_data,
                    format: parsed.metadata.format,
                    sizeBytes: parsed.metadata.size_bytes
                  });
                  notifyParent('streamFile', { filename: parsed.metadata.filename, format: parsed.metadata.format, sizeBytes: parsed.metadata.size_bytes });
                  yield { content: buildContentParts() };
                }
              } else if (parsed.type === 'message_ids') {
                userMessageId = parsed.user_message_id;
                assistantMessageId = parsed.assistant_message_id;
                notifyParent('messageIds', { userMessageId, assistantMessageId });

                setTimeout(() => {
                  const messages = document.querySelectorAll('[data-role]');
                  if (messages.length >= 2) {
                    const userMsg = messages[messages.length - 2];
                    const assistantMsg = messages[messages.length - 1];

                    if (userMsg && userMessageId) {
                      userMsg.setAttribute('data-message-id', userMessageId);
                    }
                    if (assistantMsg && assistantMessageId) {
                      assistantMsg.setAttribute('data-message-id', assistantMessageId);
                    }
                  }
                }, 100);
              } else if (parsed.type === 'error') {
                // Error from backend - append error text directly
                const errorMessage = parsed.error?.message || 'An error occurred';
                accumulatedText += (accumulatedText ? '\n\n' : '') + `**Error:** ${errorMessage}`;
                notifyParent('streamError', { message: errorMessage });
                yield { content: buildContentParts() };
              } else if (parsed.type === 'done') {
                chatEvents.dispatchEvent(new CustomEvent('tts_stream_end'));
                notifyParent('streamDone', { fullText: accumulatedText, userMessageId, assistantMessageId });
                break;
              }
            } catch (e) {
              console.error('Parse error for line:', line, e);
            }
          }
        }

        return { content: buildContentParts() };
      } catch (error) {
        if (error.name === 'AbortError') {
          return {
            content: [{ type: 'text', text: '' }]
          };
        }
        throw error;
      }
    }
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}

export default function ChatInterfaceEmbedded() {
  const messageIdsRef = useRef({ user: null, assistant: null });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [messageFiles, setMessageFiles] = useState([]);
  const threadIdRef = useRef(crypto.randomUUID());

  // Key to force runtime remount (clears chat without page reload)
  const [runtimeKey, setRuntimeKey] = useState(0);

  // History messages for loaded threads
  const [historyMessages, setHistoryMessages] = useState([]);

  // Get API URL from config
  const API_URL = window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';

  // Get URL parameters for embedded mode
  const params = new URLSearchParams(window.location.search);
  const customGptId = params.get('gpt');
  const shareToken = params.get('token');
  const customName = params.get('name');
  const customDescription = params.get('description');
  const hideHeader = params.get('hide_header') === 'true';
  const customApiUrl = params.get('api');

  // Color customization from URL parameters
  const accentColor = params.get('accent_color');
  const headerBg = params.get('header_bg');

  // State for GPT info
  const [gptInfo, setGptInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Dynamic title/description set at runtime via postMessage 'setTitle'.
  // Highest priority — overrides URL ?name= and the GPT's own name.
  const [dynamicTitle, setDynamicTitle] = useState(null);
  const [dynamicDescription, setDynamicDescription] = useState(null);
  // Voice state managed by VoiceProvider

  // Parse URL parameters for embedded configuration
  // Mode: 'readonly' (no input), 'write' (input, no buttons), 'full' (input + copy/delete buttons)
  const [embeddedConfig, setEmbeddedConfig] = useState({
    mode: 'write',
    isReadonly: false,
    hideFileUpload: true,
    hidePaste: true,
    showCopyButton: false,
    showDeleteButton: false,
    // Embedded-specific fields for API calls
    isEmbedded: true,
    shareToken: null,
    customGptId: null,
    apiUrl: null
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const mode = params.get('mode') || 'write';
    const isReadonly = mode === 'readonly';
    const isFull = mode === 'full';
    const hidePaste = params.get('hide_paste') !== 'false'; // default true (hidden)

    // File upload visibility: URL param overrides GPT setting
    // If URL param is explicitly set, use it; otherwise fall back to GPT's enable_temporary_upload
    const hideFileUploadParam = params.get('hide_file_upload');
    let hideFileUpload;
    if (hideFileUploadParam !== null) {
      // URL param explicitly set — use it as override
      hideFileUpload = hideFileUploadParam !== 'false';
    } else {
      // No URL param — default to GPT setting (hide if uploads not enabled)
      hideFileUpload = !(gptInfo?.enable_temporary_upload);
    }

    setEmbeddedConfig({
      mode,
      isReadonly,
      hideFileUpload: isReadonly || hideFileUpload,
      hidePaste: isReadonly || hidePaste,
      showCopyButton: isFull,
      showDeleteButton: isFull,
      // Embedded-specific fields for API calls
      isEmbedded: true,
      shareToken,
      customGptId,
      apiUrl: API_URL
    });
  }, [shareToken, customGptId, API_URL, gptInfo]);

  // Load GPT info (name, logo, custom CSS, etc.)
  useEffect(() => {
    if (!customGptId || !shareToken) return;

    const loadGptInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/public/gpt/${customGptId}?token=${shareToken}`);
        if (response.ok) {
          const data = await response.json();
          setGptInfo(data);

          // Update page title
          document.title = data.name || 'Chat Assistant';

          // Update favicon and apple-touch-icon if available
          // Using same approach as old favicon.js - remove old links, add new ones
          if (data.logo_url) {
            const logoUrl = `${API_URL}${data.logo_url}?t=${Date.now()}`;

            // Get existing icon links first
            const existingIcons = document.querySelectorAll('link[rel*="icon"]');

            // Create new favicon
            const newFavicon = document.createElement('link');
            newFavicon.rel = 'icon';
            newFavicon.type = 'image/png';
            newFavicon.href = logoUrl;
            document.head.appendChild(newFavicon);

            // Create new apple-touch-icon for iOS home screen
            const newAppleIcon = document.createElement('link');
            newAppleIcon.rel = 'apple-touch-icon';
            newAppleIcon.href = logoUrl;
            document.head.appendChild(newAppleIcon);

            // Remove old icons after new ones are added (prevents flicker)
            existingIcons.forEach(link => link.remove());
          }

          // Load custom CSS if available
          if (data.has_custom_css) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = `${API_URL}/public/gpt/${customGptId}/styles.css?token=${shareToken}&v=${Date.now()}`;
            document.head.appendChild(link);
          }
        } else if (response.status === 403) {
          setLoadError('This chat is no longer available. The share link may have been revoked.');
          document.title = 'Chat Unavailable';
        } else if (response.status === 404) {
          setLoadError('Chat not found. Please check the URL.');
          document.title = 'Chat Not Found';
        } else {
          setLoadError('Failed to load chat. Please try again later.');
          document.title = 'Error';
        }
      } catch (err) {
        console.error('Could not load GPT info:', err);
        setLoadError('Failed to connect to the server. Please check your connection.');
        document.title = 'Connection Error';
      } finally {
        setIsLoading(false);
      }
    };

    loadGptInfo();
  }, [customGptId, shareToken, API_URL]);

  // Refs for postMessage API
  const temporaryDocumentsRef = useRef([]);  // Currently SELECTED documents to send with next message
  const allUploadedDocsRef = useRef([]);     // ALL uploaded documents (master list)
  const temporaryDocumentsCacheRef = useRef({}); // Cache for uploaded documents (by URL/text key)
  const justUploadedRef = useRef(false);     // Flag to ignore empty setTemporaryDocuments right after upload

  // Upload temporary documents function
  const uploadTemporaryDocuments = async (items) => {
    /**
     * Upload temporary documents. Accepts:
     * - URLs: ['http://...', 'http://...']
     * - Text: [{text: '...', filename: 'doc.txt'}, ...]
     * - Mixed: ['http://...', {text: '...', filename: 'doc.txt'}]
     */
    if (!items || items.length === 0) {
      console.warn('[uploadTemporaryDocuments] No items provided');
      return [];
    }

    if (!customGptId) {
      throw new Error('Custom GPT ID not set');
    }

    // Separate URLs and text items, check cache
    const urls = [];
    const textItems = [];
    const cachedDocs = [];
    const cache = temporaryDocumentsCacheRef.current;

    for (const item of items) {
      if (typeof item === 'string') {
        // It's a URL string
        if (cache[item]) {
          cachedDocs.push(cache[item]);
        } else {
          urls.push(item);
        }
      } else if (item.url) {
        // It's a URL object with filename
        if (cache[item.url]) {
          cachedDocs.push(cache[item.url]);
        } else {
          urls.push({ url: item.url, filename: item.filename });
        }
      } else if (item.text) {
        // It's text content
        const cacheKey = `text:${item.filename}:${item.text.substring(0, 50)}`;
        if (cache[cacheKey]) {
          cachedDocs.push(cache[cacheKey]);
        } else {
          textItems.push(item);
        }
      }
    }

    // All items were cached - no need to upload, but still need to update selection
    if (urls.length === 0 && textItems.length === 0) {
      // Update master list with any new cached docs
      const existingIds = new Set(allUploadedDocsRef.current.map(d => d.file_id));
      const newDocs = cachedDocs.filter(d => !existingIds.has(d.file_id));
      if (newDocs.length > 0) {
        allUploadedDocsRef.current = [...allUploadedDocsRef.current, ...newDocs];
      }

      // Update selection to include these cached docs
      temporaryDocumentsRef.current = [...allUploadedDocsRef.current];

      // Set flag to ignore empty setTemporaryDocuments that FBO sends right after
      justUploadedRef.current = true;
      setTimeout(() => { justUploadedRef.current = false; }, 500);

      return cachedDocs;
    }

    try {
      const formData = new FormData();

      // Add URLs as JSON (backend will download)
      if (urls.length > 0) {
        formData.append('document_urls', JSON.stringify(urls));
      }

      // Add text items as JSON
      if (textItems.length > 0) {
        formData.append('documents_text', JSON.stringify(textItems));
      }

      // Build URL for public access with share token
      const uploadUrl = `${API_URL}/public/custom-gpts/${customGptId}/temporary/upload?token=${shareToken}`;

      // Upload to backend
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`Upload failed: ${uploadResponse.statusText} - ${errorText}`);
      }

      const result = await uploadResponse.json();

      // Backend returns 'files' with file_id (LLM provider ID), provider, filename, etc.
      // These are ephemeral - NOT stored in our database, just uploaded to OpenAI/Gemini
      // Format needed for temporary_file_ids: {file_id, filename, provider, content_type}
      const uploadedDocs = (result.files || []).map(f => ({
        file_id: f.file_id,  // LLM provider file ID (e.g., "file-xxxxx" for OpenAI)
        filename: f.filename,
        size: f.size,
        provider: f.provider,  // "openai" or "gemini"
        content_type: f.content_type
      }));

      // Cache the uploaded documents
      if (uploadedDocs.length > 0) {
        let urlIndex = 0;
        let textIndex = 0;

        for (const doc of uploadedDocs) {
          // Try to match back to original item for caching
          if (urlIndex < urls.length) {
            const urlItem = urls[urlIndex];
            const cacheKey = typeof urlItem === 'string' ? urlItem : urlItem.url;
            cache[cacheKey] = doc;
            urlIndex++;
          } else if (textIndex < textItems.length) {
            const textItem = textItems[textIndex];
            const cacheKey = `text:${textItem.filename}:${textItem.text.substring(0, 50)}`;
            cache[cacheKey] = doc;
            textIndex++;
          }
        }
      }

      // Files are uploaded to LLM provider (OpenAI/Gemini) but NOT stored in our database.
      // Return the file info so caller can store in temporaryDocumentsRef for the next chat message.
      const allDocs = [...cachedDocs, ...uploadedDocs];

      // Store in master list (all uploaded docs) - merge with existing
      const existingIds = new Set(allUploadedDocsRef.current.map(d => d.file_id));
      const newDocs = allDocs.filter(d => !existingIds.has(d.file_id));
      allUploadedDocsRef.current = [...allUploadedDocsRef.current, ...newDocs];

      // By default, also add to selected docs (will be sent with next message)
      temporaryDocumentsRef.current = [...allUploadedDocsRef.current];

      // Set flag to ignore empty setTemporaryDocuments that might come right after
      justUploadedRef.current = true;
      setTimeout(() => { justUploadedRef.current = false; }, 500);  // Reset after 500ms

      return allDocs;

    } catch (error) {
      console.error('[uploadTemporaryDocuments] Error:', error);
      throw error;
    }
  };

  // PostMessage API - allows parent page to control the chat
  useEffect(() => {
    const handleMessage = async (event) => {
      const { action, data } = event.data || {};

      if (!action) {
        return;
      }

      try {
        switch (action) {
          case 'sendMessage':
            if (data && data.text) {
              // TODO: Implement programmatic message sending via runtime
              // For now, we can dispatch to the input
              const input = document.querySelector('[data-aui-composer-input]') ||
                           document.querySelector('textarea');
              if (input) {
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                  window.HTMLTextAreaElement.prototype,
                  'value'
                ).set;
                nativeInputValueSetter.call(input, data.text);
                input.dispatchEvent(new Event('input', { bubbles: true }));
                // Trigger form submit
                const form = input.closest('form');
                if (form) {
                  form.dispatchEvent(new Event('submit', { bubbles: true }));
                }
              }
              event.source?.postMessage({ action: 'messageSent', success: true }, event.origin);
            }
            break;

          case 'setTemporaryDocuments':
            // Set SELECTED documents for the next message (subset of uploaded docs)
            // Format: [{file_id, filename, provider, content_type}, ...] or just [file_id, file_id, ...]
            // Pass empty array to deselect all documents
            const setFileIds = data?.files || data?.documentIds || [];

            if (setFileIds.length === 0) {
              // Empty array = deselect all, BUT ignore if we just uploaded (FBO race condition)
              if (justUploadedRef.current && allUploadedDocsRef.current.length > 0) {
                event.source?.postMessage({
                  action: 'temporaryDocumentsSet',
                  success: true,
                  selected_count: temporaryDocumentsRef.current.length,
                  total_uploaded: allUploadedDocsRef.current.length,
                  ignored_empty: true
                }, event.origin);
                break;
              }
              temporaryDocumentsRef.current = [];
            } else if (typeof setFileIds[0] === 'string') {
              // Array of file_id strings - look up full objects from master list
              const masterMap = new Map(allUploadedDocsRef.current.map(d => [d.file_id, d]));
              temporaryDocumentsRef.current = setFileIds
                .map(id => masterMap.get(id))
                .filter(Boolean);
            } else if (setFileIds[0]?.file_id) {
              // Full objects with file_id - use directly
              temporaryDocumentsRef.current = setFileIds;
            } else {
              temporaryDocumentsRef.current = [];
            }

            event.source?.postMessage({
              action: 'temporaryDocumentsSet',
              success: true,
              selected_count: temporaryDocumentsRef.current.length,
              total_uploaded: allUploadedDocsRef.current.length
            }, event.origin);
            break;

          case 'clearChat':
            // Generate new thread ID to clear chat
            threadIdRef.current = crypto.randomUUID();
            // Clear message files and history
            setMessageFiles([]);
            setHistoryMessages([]);
            // Clear temporary documents (both selected and master list)
            temporaryDocumentsRef.current = [];
            allUploadedDocsRef.current = [];
            temporaryDocumentsCacheRef.current = {};
            // Increment key to force runtime remount (clears chat without page reload)
            setRuntimeKey(prev => prev + 1);
            event.source?.postMessage({ action: 'chatCleared', success: true }, event.origin);
            break;

          case 'getThreadId':
            event.source?.postMessage({
              action: 'threadIdResponse',
              threadId: threadIdRef.current
            }, event.origin);
            break;

          case 'getTemporaryDocuments':
            // Return both the master list (all uploaded) and selected list
            event.source?.postMessage({
              action: 'temporaryDocumentsResponse',
              uploaded: allUploadedDocsRef.current || [],
              selected: temporaryDocumentsRef.current || []
            }, event.origin);
            break;

          case 'loadThread':
            if (data?.threadId) {
              const isNewThread = threadIdRef.current !== data.threadId;
              threadIdRef.current = data.threadId;

              // Only remount runtime and fetch history if switching to a different thread
              // This prevents breaking the input on initial load
              if (isNewThread) {
                // Clear message files
                setMessageFiles([]);

                // Fetch thread messages from API
                try {
                  const response = await fetch(`${API_URL}/public/cme/${customGptId}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      type: 'threads.get',
                      params: { thread_id: data.threadId, limit: 100 },
                      token: shareToken
                    })
                  });

                  if (response.ok) {
                    const thread = await response.json();
                    const items = thread.items?.data || [];

                    // Convert to format expected by Thread component
                    // Preserve content structure (string or array with images)
                    const messages = items.map(msg => ({
                      id: msg.id,
                      role: msg.role,
                      content: msg.content, // Preserve original structure (string or array with images)
                      metadata: msg.metadata,
                      createdAt: msg.created_at ? new Date(msg.created_at) : undefined
                    }));

                    setHistoryMessages(messages);
                    console.log('[PostMessage] loadThread - loaded', messages.length, 'messages');
                  } else {
                    console.warn('[PostMessage] loadThread - failed to fetch messages:', response.status);
                    setHistoryMessages([]);
                  }
                } catch (err) {
                  console.error('[PostMessage] loadThread - error fetching messages:', err);
                  setHistoryMessages([]);
                }

                // Increment key to force runtime remount (clears chat UI)
                setRuntimeKey(prev => prev + 1);
              }

              event.source?.postMessage({
                action: 'threadLoaded',
                success: true,
                threadId: data.threadId
              }, event.origin);
            }
            break;

          case 'setMode':
            console.log('[PostMessage] setMode - mode:', data?.mode);
            if (data?.mode) {
              const isReadonly = data.mode === 'readonly';
              const isFull = data.mode === 'full';
              setEmbeddedConfig(prev => ({
                ...prev,
                mode: data.mode,
                isReadonly,
                hideFileUpload: isReadonly || prev.hideFileUpload,
                hidePaste: isReadonly || prev.hidePaste,
                showCopyButton: isFull,
                showDeleteButton: isFull
              }));
              event.source?.postMessage({
                action: 'modeSet',
                success: true,
                mode: data.mode
              }, event.origin);
            }
            break;

          case 'setTitle':
            // Override header title (and optionally description) at runtime.
            // Pass null/empty to clear and fall back to URL ?name=… or the
            // GPT's own name. Use when the embed is reused for multiple
            // contexts without reloading the iframe (e.g. tab switching).
            console.log('[PostMessage] setTitle - title:', data?.title, 'description:', data?.description);
            setDynamicTitle(data?.title ?? null);
            if (data && 'description' in data) {
              setDynamicDescription(data.description ?? null);
            }
            event.source?.postMessage({
              action: 'titleSet',
              success: true,
              title: data?.title ?? null,
              description: data?.description ?? null
            }, event.origin);
            break;

          case 'uploadTemporaryDocuments':
            console.log('[PostMessage] uploadTemporaryDocuments - items:', data?.items);
            // Upload temporary documents to Qdrant (URLs, text, or files)
            if (data && data.items) {
              try {
                const uploadedDocs = await uploadTemporaryDocuments(data.items);
                console.log('[PostMessage] uploadTemporaryDocuments - completed:', uploadedDocs);
                event.source?.postMessage({
                  action: 'temporaryDocumentsUploaded',
                  success: true,
                  documents: uploadedDocs
                }, event.origin);
              } catch (error) {
                console.error('[PostMessage] uploadTemporaryDocuments - error:', error);
                event.source?.postMessage({
                  action: 'temporaryDocumentsUploaded',
                  success: false,
                  error: error.message
                }, event.origin);
              }
            } else {
              console.error('[PostMessage] uploadTemporaryDocuments - missing items');
            }
            break;

          default:
            console.warn('[PostMessage] Unknown action:', action);
        }
      } catch (error) {
        console.error('[PostMessage] Error processing action:', action, error);
        event.source?.postMessage({
          action: action + 'Error',
          success: false,
          error: error.message
        }, event.origin);
      }
    };

    window.addEventListener('message', handleMessage);

    // Debug: Add global test function
    window.testPostMessage = () => {
      console.log('✅ testPostMessage function is available');
      console.log('Thread ID:', threadIdRef.current);
      return 'PostMessage listener is working!';
    };
    console.log('🔧 Embedded chat loaded. Try: window.testPostMessage()');

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Handle conversation starter click
  const handleStarterClick = (prompt) => {
    // Find the composer input and fill it
    const textarea = document.querySelector('[placeholder="Type your message..."]');
    if (textarea) {
      // Set value
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        'value'
      ).set;
      nativeInputValueSetter.call(textarea, prompt);

      // Trigger input event
      textarea.dispatchEvent(new Event('input', { bubbles: true }));

      // Submit the form
      setTimeout(() => {
        const form = textarea.closest('form');
        if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }, 100);
    }
  };

  // Don't render until GPT info is loaded
  if (isLoading) {
    return null;
  }

  // Show error page if GPT failed to load
  if (loadError) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 4,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'error.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h3" sx={{ color: 'error.main' }}>!</Typography>
        </Box>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Chat Unavailable
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
          {loadError}
        </Typography>
      </Box>
    );
  }

  // Compute effective API URL (allow override via URL param)
  const effectiveApiUrl = customApiUrl || API_URL;

  // Compute display values (URL params override GPT info)
  // Priority: postMessage setTitle (dynamic) > URL ?name= > GPT's own name
  const displayName = dynamicTitle || customName || gptInfo?.name || 'Chat Assistant';
  const displayDescription = dynamicDescription || customDescription || gptInfo?.description;

  return (
    <FilesContext.Provider value={{ selectedFiles, setSelectedFiles }}>
      <MessageFilesContext.Provider value={{ messageFiles }}>
        <VoiceProvider apiUrl={API_URL} gptId={customGptId} shareToken={shareToken}>
        <EmbeddedConfigContext.Provider value={{
          ...embeddedConfig,
          hasVoice: !!gptInfo?.voice_config,
          hasMicrophone: !!gptInfo?.microphone_config,
        }}>
          <ChatRuntimeWrapper
            key={runtimeKey}
            threadIdRef={threadIdRef}
            temporaryDocumentsRef={temporaryDocumentsRef}
            customGptId={customGptId}
            shareToken={shareToken}
            API_URL={API_URL}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            setMessageFiles={setMessageFiles}
          >
            <div
              className="chat-container"
              style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                overflow: 'hidden',
              }}
            >
              {/* Header - conditionally hidden via hide_header param */}
              {!hideHeader && (
                <div
                  id="chat-header"
                  className="chat-header"
                  style={headerBg ? { background: headerBg } : undefined}
                >
                  <div className="chat-header-content">
                    {gptInfo?.logo_url && (
                      <img
                        id="companyLogo"
                        src={`${effectiveApiUrl}${gptInfo.logo_url}?t=${Date.now()}`}
                        alt="Logo"
                        className="chat-header-logo"
                      />
                    )}
                    <div className="chat-header-text">
                      <span id="chatTitle" className="chat-header-title">
                        {displayName}
                      </span>
                      {displayDescription && (
                        <span id="chatDescription" className="chat-header-description">
                          {displayDescription}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <Thread
                historyMessages={historyMessages}
                conversationStartersSlot={
                  !embeddedConfig.isReadonly && (
                    <ConversationStarters
                      starters={gptInfo?.conversation_starters || []}
                      onSelectStarter={handleStarterClick}
                    />
                  )
                }
              />

              {/* Powered by footer */}
              <div className="chat-powered-by">
                <div className="chat-powered-by-content">
                  <a
                    href="https://www.nnaico.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="chat-powered-by-link"
                  >
                    Powered by Insight
                  </a>
                </div>
              </div>
            </div>

            <style>{`
              /* No animation on messages to prevent scroll jump issues */
              .chat-message-enter {
                /* Animation disabled - was causing scroll jump when new messages are added */
              }

              .chat-message-bubble {
                transition: all 0.2s ease-out;
              }

              /* Custom color support - only applied when URL params are set */
              ${accentColor ? `
              #sendButton {
                background-color: ${accentColor};
              }
              #sendButton:hover {
                filter: brightness(0.9);
              }
              ` : ''}

              /* Header description styling */
              .chat-header-text {
                display: flex;
                flex-direction: column;
                gap: 2px;
              }

              .chat-header-description {
                font-size: 0.85rem;
                color: rgba(255, 255, 255, 0.8);
                font-weight: 400;
              }

              /* Dark mode description */
              .dark .chat-header-description {
                color: rgba(255, 255, 255, 0.7);
              }

            `}</style>
          </ChatRuntimeWrapper>
        </EmbeddedConfigContext.Provider>
        </VoiceProvider>
      </MessageFilesContext.Provider>
    </FilesContext.Provider>
  );
}
